import { cn } from '@/client/lib/utils';
import { useId } from 'react';

/** Signal teal — the dot. The only colour in the logo, and it never changes. */
export const SIGNAL = '#19C39C';

interface GaraMarkProps {
  /** Rendered height in px. Width follows the 48:54 shield ratio. */
  size?: number;
  className?: string;
  /** Accessible label. Omit when the mark sits next to visible "garatech" text. */
  title?: string;
}

/**
 * The GaraTech shield: "gt" knocked out of a pointed shield, with one status dot.
 * The shield is drawn in `currentColor` (Paper on dark, Ink on light) and the
 * letters are transparent, so the mark works on any background.
 */
export function GaraMark({ size = 28, className, title }: GaraMarkProps) {
  // Unique per instance: a hidden copy (e.g. the desktop rail on mobile) must not own the mask.
  const maskId = `gara-mask-${useId().replace(/:/g, '')}`;
  return (
    <svg
      width={(size * 48) / 54}
      height={size}
      viewBox="0 0 48 54"
      className={cn('shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="48" height="54">
          <rect width="48" height="54" fill="#fff" />
          <g fill="none" stroke="#000" strokeWidth="4">
            <circle cx="16" cy="20" r="6" />
            <path d="M22 12 V29 A6 6 0 0 1 10 29" />
            <path d="M33 7 V24 A5 5 0 0 0 38 29 H40 M28 14 H40" />
          </g>
          <circle cx="24" cy="44" r="3" fill="#000" />
        </mask>
      </defs>
      <polygon points="0,0 48,0 48,34 24,54 0,34" fill="currentColor" mask={`url(#${maskId})`} />
      <circle cx="24" cy="44" r="3" fill={SIGNAL} />
    </svg>
  );
}

interface GaraLockupProps {
  /** Height of the mark in px; the wordmark scales with it. */
  size?: number;
  className?: string;
}

/** Horizontal lockup: mark + lowercase "garatech" wordmark in Geist Mono 500. */
export function GaraLockup({ size = 28, className }: GaraLockupProps) {
  return (
    <span
      className={cn('inline-flex items-center', className)}
      style={{ gap: Math.round(size * 0.3) }}
    >
      <GaraMark size={size} />
      <span
        className="font-mono font-medium leading-none"
        style={{ fontSize: size * 0.574, letterSpacing: '-0.04em' }}
      >
        garatech
      </span>
    </span>
  );
}
